import * as React from 'react';
import { observer } from 'mobx-react';
import { UitkDialog, UitkDialogAnimationType, UitkDialogContent } from 'uitk-react-dialog';
import { UitkErrorSummary } from 'uitk-react-error-summary';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkIcon } from 'uitk-react-icons';
import { UitkButtonSize } from 'uitk-react-button';
import { ChooseActionButton, Phrase } from 'components/shared';
import { DatePicker } from 'components/search/wizard';
import { ActionType, FlightsIconAndPhraseType, PhraseType } from '../../../types/shared';
import { FlightsActionType } from '__generated__/api';
import { FlightsSearchFormDateFieldType } from 'types/search/wizard';

interface ForcedChoiceModalDialogProps {
  actions: ActionType[];
  animation: string;
  dialogId: string;
  handleActionSelection: (flightsActionType: FlightsActionType, e: Event) => void;
  header: string;
  annotation: PhraseType;
  checkInDate?: FlightsSearchFormDateFieldType;
  checkOutDate?: FlightsSearchFormDateFieldType;
  displayCounter?: number;
  hotelDatesTitle?: string;
  subHeader: FlightsIconAndPhraseType;
  flightType: string;
  updateHotelDates?: () => void;
  defaultCheckInDate?: FlightsSearchFormDateFieldType;
}

export const ForcedChoiceModalDialog = observer(
  ({
    animation,
    dialogId,
    handleActionSelection,
    header,
    hotelDatesTitle,
    annotation,
    subHeader,
    checkInDate,
    checkOutDate,
    actions,
    flightType,
    updateHotelDates,
    defaultCheckInDate
  }: ForcedChoiceModalDialogProps) => {
    const [datePickerRefs, setDatePickerRefs] = React.useState([]);
    const onSetDatePickerRefs = (refData: React.Ref<HTMLButtonElement>[]) => {
      setDatePickerRefs(refData);
    };

    const error = (checkInDate && checkInDate.currentError) || (checkOutDate && checkOutDate.currentError);
    const linkClickFocusId = checkInDate && checkInDate.currentError ? 'start-date-1' : 'end-date-1';
    const linkClickFocusRef = checkInDate && checkInDate.currentError ? datePickerRefs[0] : datePickerRefs[1];

    return (
      <UitkDialog animation={UitkDialogAnimationType[animation]} className="all-x-padding-two all-y-padding-two">
        <div data-test-id="xSellHotelForcedChoice">
          <UitkDialogContent key={`${dialogId}-content`}>
            <p className="uitk-type-heading-600" key={`${dialogId}-header`} data-test-id="xSellHotelForcedChoice-title">
              {header}
            </p>
            {error && (
              <div className="all-t-padding-three">
                <UitkErrorSummary
                  key={`${dialogId}-error-summary`}
                  heading={
                    checkInDate &&
                    checkInDate.possibleErrorsHeader &&
                    checkInDate.possibleErrorsHeader.label.completeText
                  }
                  link={error}
                  linkClickFocus={linkClickFocusRef}
                  linkClickFocusId={linkClickFocusId}
                />
              </div>
            )}
            <div className="uitk-type-300 all-t-padding-three" data-test-id="includes-flight-message">
              <UitkIcon
                description={subHeader.icon.description}
                id={subHeader.icon.id}
                name={subHeader.icon.id}
                className="all-r-margin-one"
              />
              {subHeader.label.completeText}
            </div>
            <p className="uitk-type-heading-400 all-t-padding-six">{hotelDatesTitle}</p>
          </UitkDialogContent>
          <div className="all-x-padding-four">
            <div className="uitk-flex-grow-2">
              <DatePicker
                startDate={checkInDate}
                endDate={checkOutDate}
                flightType={flightType}
                singleMode={false}
                identifier="FCM"
                onSubmit={updateHotelDates}
                buttonText={checkInDate.doneAction}
                singleDateSelect={false}
                onSetDatePickerRefs={onSetDatePickerRefs}
                validStartDate={defaultCheckInDate && defaultCheckInDate.date && defaultCheckInDate.date.isoFormat}
                nestedInDialog={true}
              />
            </div>
            <div className="all-t-padding-three all-b-margin-six">
              <UitkLayoutFlex space="three" wrap="wrap">
                <>
                  {actions.map((action: ActionType, index: number) => {
                    return (
                      <UitkLayoutFlexItem minWidth="full_width" key={index}>
                        <ChooseActionButton
                          action={action}
                          testDataId={index === 0 ? 'xsellAddHotelNow' : 'forcedChoiceNoThanks'}
                          handleActionSelection={handleActionSelection}
                          size={UitkButtonSize.LARGE}
                          isFullWidth={true}
                          actionUrl={
                            action.type === FlightsActionType.GO_TO_PACKAGES
                              ? action.relativePackageableURI && action.relativePackageableURI.relativePath
                              : action.relativeURI && action.relativeURI.relativePath
                          }
                        />
                      </UitkLayoutFlexItem>
                    );
                  })}
                </>
              </UitkLayoutFlex>
            </div>
            <p className="uitk-type-paragraph-200 all-b-padding-four">
              <Phrase {...annotation} />
            </p>
          </div>
        </div>
      </UitkDialog>
    );
  }
);
